import * as Yup from "yup";
import { Helmet } from "react-helmet-async";
// @mui
import { LoadingButton } from "@mui/lab";
import { Container, Grid, Card, Stack, Typography, Alert } from "@mui/material";
// components
import { useSettingsContext } from "../components/settings";
import { useSnackbar } from "../components/snackbar";
import FormProvider, { RHFTextField } from "../components/hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { backfillUserRequest } from "../controllers";

// ----------------------------------------------------------------------

export default function Backfill() {
  const { themeStretch } = useSettingsContext();
  const { enqueueSnackbar } = useSnackbar();

  const defaultValues = {
    sourceUserId: "",
    destinationUserId: "",
  };

  const BackfillSchema = Yup.object().shape({
    sourceUserId: Yup.string().required("sourceUserId is required"),
    destinationUserId: Yup.string().required("destinationUserId is required"),
  });

  const methods = useForm({
    resolver: yupResolver(BackfillSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = methods;

  const onSubmit = async (data) => {
    backfillUserRequest({
      sourceUserId: data.sourceUserId,
      destinationUserId: data.destinationUserId,
    }).then(() => {
      reset();
      enqueueSnackbar(
        `Successfully backfilled user data: ${data.sourceUserId} to ${data.destinationUserId}`
      );
    }).catch((error) => {
      console.error(error);
      enqueueSnackbar(
        `Could not backfill user data: ${data.sourceUserId} to ${data.destinationUserId}`,
        { variant: "error" }
      );
    });
  };

  return (
    <>
      <Helmet>
        <title> Backfill User Data | Admin Portal</title>
      </Helmet>

      <Container maxWidth={themeStretch ? false : "xl"}>
        <Typography variant="h3" component="h1" paragraph>
          Backfill User Data
        </Typography>
        <Stack spacing={3} sx={{ mb: 3 }}>
          <Alert severity="error">
            This tool is extremely dangerous if not used properly. Please use
            with caution. This tool will copy all data from the source user to
            the destination user. This includes all user data, including
            sensitive information. Please ensure that the destination user is a
            new user with no existing data.
          </Alert>
        </Stack>

        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card sx={{ p: 3 }}>
                <Stack spacing={3}>
                  <RHFTextField
                    name="sourceUserId"
                    label="Source userId"
                    required
                  />
                  <RHFTextField
                    name="destinationUserId"
                    label="Destination userId"
                    required
                  />
                </Stack>
              </Card>
              <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                <LoadingButton
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  loading={isSubmitting}
                  disabled={!isValid}
                >
                  Backfill
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </FormProvider>
      </Container>
    </>
  );
}
